import { cn } from '@/lib/utils';
import { Badge, BADGES, BadgeCard } from './BadgeCard';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Trophy } from 'lucide-react';

// Points awarded per badge
export const BADGE_POINTS: Record<string, number> = {
  welcome: 50,
  explorer: 100,
  gourmet: 75,
  faithful: 150,
  contributor: 100,
  ambassador: 80,
  critic: 120,
  pioneer: 200,
  globetrotter: 150,
};

interface BadgeGridProps {
  unlockedBadgeIds: string[];
  getBadgeProgress: (badgeId: string) => number;
  getUnlockedAt?: (badgeId: string) => string | undefined;
  className?: string;
}

export const BadgeGrid = ({
  unlockedBadgeIds,
  getBadgeProgress,
  getUnlockedAt,
  className,
}: BadgeGridProps) => {
  const unlockedCount = unlockedBadgeIds.length;
  const totalCount = BADGES.length;

  // Find next badge to unlock
  const nextBadge = BADGES.find((badge) => {
    if (unlockedBadgeIds.includes(badge.id)) return false;
    const progress = getBadgeProgress(badge.id);
    return progress > 0 && progress < badge.requirement;
  });

  return (
    <div className={cn("space-y-3", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">Mes Badges</span>
          <span className="text-xs text-muted-foreground">
            ({unlockedCount}/{totalCount})
          </span>
        </div>
      </div>

      {/* Compact Badge Grid */}
      <div className="flex flex-wrap gap-2">
        {BADGES.map((badge) => {
          const isUnlocked = unlockedBadgeIds.includes(badge.id);
          const progress = getBadgeProgress(badge.id);
          const unlockedAt = getUnlockedAt?.(badge.id);

          return (
            <Tooltip key={badge.id}>
              <TooltipTrigger asChild>
                <div className="cursor-pointer">
                  <BadgeCard
                    badge={badge}
                    isUnlocked={isUnlocked}
                    progress={progress}
                    unlockedAt={unlockedAt}
                    compact
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold">{badge.name}</p>
                  <p className="text-xs text-muted-foreground">{badge.description}</p>
                  <p className="text-xs text-primary">
                    +{BADGE_POINTS[badge.id] || 0} pts
                  </p>
                  {!isUnlocked && (
                    <p className="text-xs">
                      Progression : {progress}/{badge.requirement}
                    </p>
                  )}
                  {isUnlocked && unlockedAt && (
                    <p className="text-xs text-green-500">
                      ✓ Débloqué le {new Date(unlockedAt).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      {/* Next Badge Progress */}
      {nextBadge && (
        <div className="text-xs text-muted-foreground">
          Prochain : <span className="font-medium">{nextBadge.name}</span>
          <span className="ml-1">
            ({getBadgeProgress(nextBadge.id)}/{nextBadge.requirement})
          </span>
        </div>
      )}
    </div>
  );
};
